import * as React from "react";
import { cn } from "@/lib/utils";

interface Stat {
  id: string;
  value: string;
  label: string;
  trend?: string;
}

interface StatsProps {
  stats: Stat[];
}

/**
 * Stats (Neo-Brutalist Version)
 *
 * Displays key metrics in cards with:
 * - Hard shadows that change color on hover
 * - Bold typography
 * - Animated entrance on scroll
 */
// Accent colors for each stat card
const accentColors = [
  { shadow: "hover:shadow-[8px_8px_0_0_#FFDC00]", badge: "bg-[#FFDC00]" }, // Yellow
  { shadow: "hover:shadow-[8px_8px_0_0_#FF73A9]", badge: "bg-[#FF73A9]" }, // Pink
  { shadow: "hover:shadow-[8px_8px_0_0_#3F5EF4]", badge: "bg-[#3F5EF4] text-white" }, // Blue
  { shadow: "hover:shadow-[8px_8px_0_0_#00EB90]", badge: "bg-[#00EB90]" }, // Green
];

export function Stats({ stats }: StatsProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 lg:py-24 bg-[#FFFBF0]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Divider line */}
        <div className="h-1 bg-black mb-16 lg:mb-24" />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat, index) => {
            const colorIndex = index % accentColors.length;
            const colors = accentColors[colorIndex];

            return (
              <div
                key={stat.id}
                className={cn(
                  "text-center group p-6 rounded-2xl",
                  "bg-white border-3 border-black",
                  "shadow-[6px_6px_0_0_#000000]",
                  "transition-all duration-200 ease-out",
                  "hover:-translate-x-1 hover:-translate-y-1",
                  colors.shadow,
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                )}
                style={{
                  transitionDelay: isVisible ? `${index * 100}ms` : "0ms",
                }}
              >
                {/* Value */}
                <div className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight font-['Epilogue'] text-[#1A1A1A]">
                  {stat.value}
                </div>

                {/* Label */}
                <div className="mt-2 text-sm sm:text-base text-[#666666] font-['Sora']">
                  {stat.label}
                </div>

                {/* Trend indicator */}
                {stat.trend && (
                  <div
                    className={cn(
                      "mt-3 inline-flex items-center gap-1",
                      "px-3 py-1 rounded-full",
                      "border-2 border-black",
                      "text-xs font-bold font-['Sora']",
                      colors.badge
                    )}
                  >
                    {stat.trend}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Divider line */}
        <div className="h-1 bg-black mt-16 lg:mt-24" />
      </div>
    </section>
  );
}
